import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText } from 'lucide-react';
import './LegalPages.css';

const Terms = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            <span>{t('terms.backButton')}</span>
          </button>
          <div className="legal-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/assets/logo/alpenscreen-horizontal-transparent.svg" alt="AlpenScreen" className="logo-img" />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-header-section">
            <FileText size={48} color="#2B7FD7" />
            <h1 className="legal-title">{t('terms.title')}</h1>
            <p className="legal-date">{t('terms.lastUpdated')}</p>
          </div>

          {/* Section 1: Scope */}
          <section className="legal-section">
            <h2>{t('terms.section1.title')}</h2>
            <p>{t('terms.section1.paragraph1')}</p>
            <p>{t('terms.section1.paragraph2')}</p>
          </section>

          {/* Section 2: Subject Matter */}
          <section className="legal-section">
            <h2>{t('terms.section2.title')}</h2>
            
            <h3>{t('terms.section2.services.title')}</h3>
            <p>{t('terms.section2.services.description')}</p>
            <p>{t('terms.section2.services.includes')}</p>
            <ul>
              <li>{t('terms.section2.services.feature1')}</li>
              <li>{t('terms.section2.services.feature2')}</li>
              <li>{t('terms.section2.services.feature3')}</li>
              <li>{t('terms.section2.services.feature4')}</li>
              <li>{t('terms.section2.services.feature5')}</li>
              <li>{t('terms.section2.services.feature6')}</li>
            </ul>

            <h3>{t('terms.section2.availability.title')}</h3>
            <p>{t('terms.section2.availability.description')}</p>

            <h3>{t('terms.section2.changes.title')}</h3>
            <p>{t('terms.section2.changes.description')}</p>
          </section>

          {/* Section 3: Contract Formation */}
          <section className="legal-section">
            <h2>{t('terms.section3.title')}</h2>
            
            <h3>{t('terms.section3.registration.title')}</h3>
            <p>{t('terms.section3.registration.paragraph1')}</p>
            <p>{t('terms.section3.registration.paragraph2')}</p>

            <h3>{t('terms.section3.requirements.title')}</h3>
            <p>{t('terms.section3.requirements.eligible')}</p>
            <ul>
              <li>{t('terms.section3.requirements.type1')}</li>
              <li>{t('terms.section3.requirements.type2')}</li>
            </ul>
            <p>{t('terms.section3.requirements.truthful')}</p>

            <h3>{t('terms.section3.account.title')}</h3>
            <p>{t('terms.section3.account.description')}</p>
          </section>

          {/* Section 4: Free Trial */}
          <section className="legal-section">
            <h2>{t('terms.section4.title')}</h2>
            <p>{t('terms.section4.paragraph1')}</p>
            <p>{t('terms.section4.paragraph2')}</p>
          </section>

          {/* Section 5: Pricing and Payment */}
          <section className="legal-section">
            <h2>{t('terms.section5.title')}</h2>
            
            <h3>{t('terms.section5.pricing.title')}</h3>
            <p>{t('terms.section5.pricing.paragraph1')}</p>
            <p><strong>{t('terms.section5.pricing.currentPrice')}</strong> {t('terms.section5.pricing.priceAmount')}</p>

            <h3>{t('terms.section5.payment.title')}</h3>
            <p>{t('terms.section5.payment.paragraph1')}</p>
            <p>{t('terms.section5.payment.paragraph2')}</p>

            <h3>{t('terms.section5.priceChanges.title')}</h3>
            <p>{t('terms.section5.priceChanges.description')}</p>
          </section>

          {/* Section 6: Contract Duration and Termination */}
          <section className="legal-section">
            <h2>{t('terms.section6.title')}</h2>
            
            <h3>{t('terms.section6.duration.title')}</h3>
            <p>{t('terms.section6.duration.description')}</p>

            <h3>{t('terms.section6.ordinary.title')}</h3>
            <p>{t('terms.section6.ordinary.description')}</p>

            <h3>{t('terms.section6.extraordinary.title')}</h3>
            <p>{t('terms.section6.extraordinary.paragraph1')}</p>
            <ul>
              <li>{t('terms.section6.extraordinary.reason1')}</li>
              <li>{t('terms.section6.extraordinary.reason2')}</li>
              <li>{t('terms.section6.extraordinary.reason3')}</li>
            </ul>

            <h3>{t('terms.section6.consequences.title')}</h3>
            <p>{t('terms.section6.consequences.description')}</p>
          </section>

          {/* Section 7: Customer Obligations */}
          <section className="legal-section">
            <h2>{t('terms.section7.title')}</h2>
            
            <h3>{t('terms.section7.lawful.title')}</h3>
            <p>{t('terms.section7.lawful.description')}</p>
            <ul>
              <li>{t('terms.section7.lawful.prohibited1')}</li>
              <li>{t('terms.section7.lawful.prohibited2')}</li>
              <li>{t('terms.section7.lawful.prohibited3')}</li>
              <li>{t('terms.section7.lawful.prohibited4')}</li>
              <li>{t('terms.section7.lawful.prohibited5')}</li>
            </ul>

            <h3>{t('terms.section7.content.title')}</h3>
            <p>{t('terms.section7.content.description')}</p>

            <h3>{t('terms.section7.indemnification.title')}</h3>
            <p>{t('terms.section7.indemnification.description')}</p>
          </section>

          {/* Section 8: Provider Rights */}
          <section className="legal-section">
            <h2>{t('terms.section8.title')}</h2>
            
            <h3>{t('terms.section8.suspension.title')}</h3>
            <p>{t('terms.section8.suspension.description')}</p>

            <h3>{t('terms.section8.usage.title')}</h3>
            <p>{t('terms.section8.usage.description')}</p>
          </section>

          {/* Section 9: Liability */}
          <section className="legal-section">
            <h2>{t('terms.section9.title')}</h2>
            
            <h3>{t('terms.section9.provider.title')}</h3>
            <p>{t('terms.section9.provider.paragraph1')}</p>
            <p>{t('terms.section9.provider.paragraph2')}</p>
            <p>{t('terms.section9.provider.paragraph3')}</p>

            <h3>{t('terms.section9.customer.title')}</h3>
            <p>{t('terms.section9.customer.description')}</p>
          </section>

          {/* Section 10: Data Protection */}
          <section className="legal-section">
            <h2>{t('terms.section10.title')}</h2>
            <p>
              {t('terms.section10.description')}{' '}
              <button onClick={() => navigate('/privacy')} className="inline-link">
                {t('terms.section10.privacyLink')}
              </button>.
            </p>
          </section>

          {/* Section 11: Changes to Terms */}
          <section className="legal-section">
            <h2>{t('terms.section11.title')}</h2>
            <p>{t('terms.section11.paragraph1')}</p>
            <p>{t('terms.section11.paragraph2')}</p>
          </section>

          {/* Section 12: Final Provisions */}
          <section className="legal-section">
            <h2>{t('terms.section12.title')}</h2>
            
            <h3>{t('terms.section12.law.title')}</h3>
            <p>{t('terms.section12.law.description')}</p>

            <h3>{t('terms.section12.jurisdiction.title')}</h3>
            <p>{t('terms.section12.jurisdiction.description')}</p>

            <h3>{t('terms.section12.severability.title')}</h3>
            <p>{t('terms.section12.severability.description')}</p>

            <h3>{t('terms.section12.assignment.title')}</h3>
            <p>{t('terms.section12.assignment.description')}</p>
          </section>

          {/* Section 13: Contact */}
          <section className="legal-section">
            <h2>{t('terms.section13.title')}</h2>
            <p>{t('terms.section13.description')}</p>
            <div className="info-box">
              <strong>AlpenScreen</strong><br />
              {t('terms.section13.email')}<br />
              {t('terms.section13.phone')}
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>{t('terms.footer.copyright')}</p>
          <div className="legal-footer-links">
            <button onClick={() => navigate('/support')} className="footer-link">
              {t('terms.footer.support')}
            </button>
            <span>•</span>
            <button onClick={() => navigate('/privacy')} className="footer-link">
              {t('terms.footer.privacy')}
            </button>
            <span>•</span>
            <button onClick={() => navigate('/imprint')} className="footer-link">
              {t('terms.footer.imprint')}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;